import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LucideIcon } from 'lucide-react-native';
import { memo } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';

export type BiButtonTone = 'primary' | 'secondary' | 'danger' | 'quiet';

const kColors: Record<
  BiButtonTone,
  { background: string; foreground: string; border: string }
> = {
  primary: {
    background: aiColors.primary,
    foreground: aiColors.primaryForeground,
    border: aiColors.primary,
  },
  secondary: {
    background: aiColors.card,
    foreground: aiColors.foreground,
    border: aiColors.border,
  },
  danger: {
    background: aiColors.destructive,
    foreground: aiColors.primaryForeground,
    border: aiColors.destructive,
  },
  quiet: {
    background: 'transparent',
    foreground: aiColors.foreground,
    border: 'transparent',
  },
};

/**
 * A raised button's shadow: darker than a resting card's, since it floats
 * over content that keeps moving under it.
 */
const kRaisedShadow = {
  shadowColor: '#0F172A',
  shadowOpacity: 0.24,
  shadowRadius: 14,
  shadowOffset: { width: 0, height: 6 },
  elevation: 10,
} as const;

/** A header or panel action: an icon, a label, or both. */
export const BiButton = memo(
  ({
    title,
    icon: Icon,
    tone = 'secondary',
    loading,
    disabled,
    accessibilityLabel,
    minWidth,
    minHeight,
    raised,
    onPress,
  }: {
    title?: string;
    icon?: LucideIcon;
    tone?: BiButtonTone;
    loading?: boolean;
    disabled?: boolean;
    accessibilityLabel?: string;
    /** Wider than its label, e.g. a sheet's floating actions. */
    minWidth?: number;
    /** Taller than a header's button, e.g. a sheet's main actions. */
    minHeight?: number;
    /** Lifted off what scrolls under it, e.g. floating over a list. */
    raised?: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const colors = kColors[tone];
    const inactive = disabled || loading;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? title}
        accessibilityState={{ disabled: !!inactive, busy: !!loading }}
        disabled={inactive}
        onPress={onPress}
        hitSlop={4}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: rpx(6),
          minHeight: minHeight ?? rpx(34),
          minWidth: minWidth ?? rpx(34),
          paddingHorizontal: title ? rpx(12) : rpx(8),
          borderRadius: rpx(10),
          borderWidth: 1,
          borderColor: colors.border,
          backgroundColor: colors.background,
          opacity: disabled ? 0.45 : pressed ? 0.8 : 1,
          ...(raised ? kRaisedShadow : null),
        })}
      >
        {loading ? (
          <ActivityIndicator size="small" color={colors.foreground} />
        ) : Icon ? (
          <Icon size={16} color={colors.foreground} />
        ) : null}
        {title ? (
          <Typography.Text medium size={rpx(12)} color={colors.foreground}>
            {title}
          </Typography.Text>
        ) : null}
      </Pressable>
    );
  },
);
